import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";
import { POLES } from "@/data/content";
import { useLang, POLE_EN, localizePrestation } from "@/i18n";

export default function Prestations() {
  const [items, setItems] = useState([]);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/prestations").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const groups = POLES.map((pole) => ({
    ...pole,
    list: items.length > 0
      ? items.filter((p) => p.pole.toLowerCase() === pole.name.toLowerCase()).map((p) => localizePrestation(p, lang))
      : pole.prestations,
  }));

  return (
    <div>
      <Nav />
      <main className="pt-36 pb-28 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="mb-20">
            <Overline>{t("ov.prestations")}</Overline>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter uppercase mb-6">
              {t("pr.h1")}
            </h1>
            <p className="text-white/60 max-w-2xl leading-relaxed">
              {t("pr.intro")}
            </p>
          </Reveal>

          {groups.map((group, gi) => (
            <div key={group.id} className="mb-20" data-testid={`pole-section-${group.id}`}>
              <Reveal className="flex items-baseline gap-6 mb-8">
                <span className="font-mono text-gold/60 text-sm">0{gi + 1}</span>
                <h2 className="font-serif text-2xl lg:text-3xl font-light">
                  {lang === "en" ? POLE_EN[group.id].name : group.name}
                </h2>
                <span className="text-white/40 text-sm hidden sm:inline">
                  {lang === "en" ? POLE_EN[group.id].tagline : group.tagline}
                </span>
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {group.list.map((p, i) => (
                  <Reveal key={p.slug} delay={i * 0.05}>
                    <Link to={`/prestations/${p.slug}`} data-testid={`prestation-card-${p.slug}`}
                      className="beam-card block h-full">
                      <div className="beam-inner p-8 h-full flex flex-col justify-between gap-10 group">
                        <div>
                          <span className="font-mono text-xs text-gold/70 tracking-[0.2em]">
                            /{p.index || String(p.order).padStart(2, "0")}
                          </span>
                          <h3 className="font-serif text-2xl font-light mt-3 mb-3">{p.title}</h3>
                          {p.accroche && <p className="text-white/50 text-sm italic">{p.accroche}</p>}
                        </div>
                        <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 group-hover:text-gold">
                          {t("pr.card")}
                          <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}

          <Reveal className="mt-8 border-t border-white/10 pt-16 text-center">
            <h2 className="font-serif text-3xl lg:text-4xl font-light mb-4">
              {t("pr.bottomH2")}
            </h2>
            <p className="text-white/60 mb-8">{t("pr.bottomP")}</p>
            <Link to="/contact" data-testid="prestations-cta-contact"
              className="inline-flex items-center gap-2 bg-gold text-black px-10 py-4 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-300 hover:bg-gold-light">
              {t("pr.bottomCta")} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </main>
      <Footer />
    </div>
  );
}
